import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const ESTADOS = {
  en_proceso: { label: 'En proceso', color: 'info' },
  atendido:   { label: 'Atendido',   color: 'warning' },
  cerrado:    { label: 'Cerrado',    color: 'success' },
};

const fmtFecha = (f) => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? f : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function TicketsPage() {
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('');
  const [selected, setSelected] = useState(null);
  const [estado, setEstado]     = useState('en_proceso');
  const [detalle, setDetalle]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState('');
  const [error, setError]       = useState('');
  const [imagenes, setImagenes] = useState([]);
  const [ampliada, setAmpliada] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tickets', { params: { status: filter || undefined } });
      setTickets(res.data);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const notify = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else          { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }
  };

  const open = async (t) => {
    setSelected(t);
    setEstado(t.estado || 'en_proceso');
    setDetalle(t.detalle_solucion || '');
    setImagenes([]);
    if (t.tiene_imagenes) {
      try {
        const res = await api.get(`/tickets/${t.id}/images`);
        setImagenes(res.data.imagenes || []);
      } catch { /* sin imágenes si falla la carga */ }
    }
  };

  const guardar = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/tickets/${selected.id}`, { estado, detalle_solucion: detalle });
      notify('Ticket actualizado');
      setSelected(null);
      load();
    } catch (err) {
      notify(err.response?.data?.error || 'Error guardando el ticket', true);
    } finally { setSaving(false); }
  };

  const infoRow = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.85rem' }}>
      <span style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ textAlign: 'right', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Soporte</h1>
          <p className="page-subtitle">Tickets de soporte de los negocios</p>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}
      {error   && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {[['', 'Todos'], ['en_proceso', 'En proceso'], ['atendido', 'Atendidos'], ['cerrado', 'Cerrados']].map(([v, l]) => (
          <button key={v} className={`btn ${filter === v ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: '0.85rem' }} onClick={() => setFilter(v)}>{l}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.2fr' : '1fr', gap: '1.5rem' }}>
        {/* Lista */}
        <div className="card">
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>No hay tickets</div>
          ) : (
            tickets.map(t => {
              const est = ESTADOS[t.estado] || { label: t.estado, color: 'secondary' };
              return (
                <div
                  key={t.id}
                  onClick={() => open(t)}
                  style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid var(--color-border)',
                    cursor: 'pointer',
                    background: selected?.id === t.id ? 'rgba(201,168,76,0.08)' : 'transparent',
                    borderLeft: selected?.id === t.id ? '3px solid var(--color-gold)' : '3px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.business_name || 'Sin negocio'}</div>
                    <span className={`badge badge-${est.color}`} style={{ fontSize: '0.65rem', flexShrink: 0 }}>{est.label}</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--color-text)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.descripcion}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    {t.user_name} · {fmtFecha(t.creado_en)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detalle */}
        {selected && (
          <div className="card" style={{ alignSelf: 'start' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Ticket #{selected.id}</h3>
              <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                {infoRow('Reportado por', selected.user_name)}
                {infoRow('Negocio', selected.business_name)}
                {infoRow('Administrador del negocio', selected.admin_name)}
                {infoRow('Fecha del ticket', fmtFecha(selected.creado_en))}
              </div>

              <div className="form-group">
                <label className="form-label">Descripción de la novedad</label>
                <div style={{ background: 'var(--color-surface)', borderRadius: 8, padding: '1rem', fontSize: '0.9rem', lineHeight: 1.6 }}>
                  {selected.descripcion}
                </div>
              </div>

              {imagenes.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Imágenes adjuntas</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {imagenes.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Adjunto ${i + 1}`}
                        onClick={() => setAmpliada(img)}
                        style={{
                          width: 90, height: 90, objectFit: 'cover', borderRadius: 8,
                          border: '1px solid var(--color-border)', cursor: 'zoom-in',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={estado} onChange={e => setEstado(e.target.value)}>
                  <option value="en_proceso">En proceso</option>
                  <option value="atendido">Atendido</option>
                  <option value="cerrado">Cerrado</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Detalle de la solución</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={detalle}
                  onChange={e => setDetalle(e.target.value)}
                  placeholder="Describe cómo se resolvió la novedad..."
                />
              </div>

              <button className="btn btn-primary" onClick={guardar} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Visor de imagen ampliada */}
      {ampliada && (
        <div
          onClick={() => setAmpliada(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: '2rem',
          }}
        >
          <img src={ampliada} alt="Imagen ampliada" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}
